import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Drawer,
    Menu,
    MenuItem,
    Divider,
    Dialog as MuiDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Select,
    FormControl,
    Checkbox,
    Chip,
    ListItemText,
    TablePagination,
    Tooltip,
    CircularProgress,
    LinearProgress,
    Rating
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import StarIcon from '@mui/icons-material/Star';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

import conversationHistoryService from '../services/ConversationHistoryService';
import HelpButton from './HelpButton';
import chatbotService from '../services/ChatbotService';

const CITATION_RE = /\[([^[\]]+?):([a-f0-9]{20,})\/(\d+)\]/g;

function parseCitations(text) {
    if (!text) return text;
    const parts = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(CITATION_RE.source, 'g');
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        parts.push({ type: 'citation', fileName: match[1], documentId: match[2], page: match[3] });
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : text;
}

function CitationChip({ fileName, documentId, page, onPreview }) {
    const label = `${fileName}, p.${page}`;
    return (
        <Tooltip title={label} arrow>
            <Box
                component="span"
                onClick={(e) => { e.stopPropagation(); onPreview(documentId, page, fileName); }}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.3,
                    bgcolor: 'rgba(25,118,210,0.08)',
                    borderRadius: '4px',
                    px: 0.6,
                    py: 0.15,
                    mx: 0.25,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#1976d2',
                    verticalAlign: 'middle',
                    lineHeight: 1.4,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(25,118,210,0.16)' },
                }}
            >
                <DescriptionIcon sx={{ fontSize: 13 }} />
                {fileName.length > 25 ? fileName.slice(0, 22) + '...' : fileName}
                <Box component="span" sx={{ color: '#1976d2', opacity: 0.7 }}>p.{page}</Box>
            </Box>
        </Tooltip>
    );
}

function renderTextWithCitations(text, onPreview) {
    const parts = parseCitations(text);
    if (typeof parts === 'string') return parts;
    return parts.map((part, i) =>
        typeof part === 'string' ? part : <CitationChip key={i} fileName={part.fileName} documentId={part.documentId} page={part.page} onPreview={onPreview} />
    );
}

function getFeedbackDescriptor(feedbackRating, feedbackType) {
    const raw = String(feedbackRating || '').trim();
    if (!raw) return null;

    const lower = raw.toLowerCase();
    const normalized = lower.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const compact = lower.replace(/[^a-z0-9]+/g, '');
    const normalizedType = String(feedbackType || '').trim().toLowerCase();
    const compactType = normalizedType.replace(/[^a-z0-9]+/g, '');
    const configuredForStars = compactType === 'stars' || compactType === 'star';
    const configuredForThumbs = compactType === 'thumbs' || compactType === 'thumb';

    const toStarDescriptor = () => {
        const starMatch = lower.match(/\b([1-5])(?:\s*(?:star|stars))?\b/) || lower.match(/([1-5])/);
        const value = starMatch ? Number(starMatch[1]) : 0;
        return {
            type: 'stars',
            value,
            label: value ? `${value} ${value === 1 ? 'Star' : 'Stars'}` : raw,
        };
    };

    const toThumbDescriptor = (allowNumeric = false) => {
        if (
            ['thumbs_down', 'thumb_down', 'dislike', 'disliked', 'negative', 'bad', 'down', 'no'].includes(normalized)
            || ['thumbsdown', 'thumbdown'].includes(compact)
            || (allowNumeric && (lower.startsWith('1') || lower.startsWith('2')))
        ) {
            return { type: 'thumb', sentiment: 'negative', label: 'Thumbs down' };
        }
        if (
            ['thumbs_up', 'thumb_up', 'like', 'liked', 'positive', 'good', 'up', 'yes'].includes(normalized)
            || ['thumbsup', 'thumbup'].includes(compact)
            || (allowNumeric && (lower.startsWith('4') || lower.startsWith('5')))
        ) {
            return { type: 'thumb', sentiment: 'positive', label: 'Thumbs up' };
        }
        return null;
    };

    const explicitThumbDescriptor = toThumbDescriptor(false);
    if (explicitThumbDescriptor) return explicitThumbDescriptor;

    if (configuredForStars) return toStarDescriptor();
    if (configuredForThumbs) return toThumbDescriptor(true) || { type: 'text', label: raw };

    if (lower.includes('star') || /^[1-5]$/.test(lower)) return toStarDescriptor();

    const thumbDescriptor = toThumbDescriptor(false);
    if (thumbDescriptor) return thumbDescriptor;

    return { type: 'text', label: raw };
}

function FeedbackDisplay({ rating, message, feedbackType }) {
    const descriptor = getFeedbackDescriptor(rating, feedbackType);
    const hasMessage = Boolean(message);

    if (!descriptor && !hasMessage) return null;

    const isNegative = descriptor?.sentiment === 'negative' || descriptor?.value <= 2;
    const stateColor = isNegative ? '#b42318' : '#067647';
    const stateBg = isNegative ? 'rgba(180,35,24,0.1)' : 'rgba(6,118,71,0.1)';

    return (
        <Box sx={{ mt: 0.75 }}>
            {descriptor && (
                <Tooltip title={descriptor.label} arrow>
                    <Box
                        aria-label={descriptor.label}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 0.8,
                            py: 0.35,
                            borderRadius: '4px',
                            bgcolor: descriptor.type === 'text' ? 'rgba(34,34,34,0.08)' : stateBg,
                            color: descriptor.type === 'text' ? 'text.secondary' : stateColor,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            lineHeight: 1.2,
                        }}
                    >
                        {descriptor.type === 'stars' ? (
                            <Rating
                                value={descriptor.value}
                                max={5}
                                readOnly
                                size="small"
                                sx={{
                                    '& .MuiRating-iconFilled': { color: '#f59e0b' },
                                    '& .MuiRating-iconEmpty': { color: 'rgba(34,34,34,0.22)' },
                                }}
                            />
                        ) : descriptor.type === 'thumb' ? (
                            descriptor.sentiment === 'negative' ? (
                                <ThumbDownAltOutlinedIcon sx={{ fontSize: 16 }} />
                            ) : (
                                <ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} />
                            )
                        ) : (
                            <Box component="span">{descriptor.label}</Box>
                        )}
                    </Box>
                </Tooltip>
            )}
            {hasMessage && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.45,
                        opacity: 0.86,
                        fontStyle: 'italic',
                        wordBreak: 'break-word',
                    }}
                >
                    {message}
                </Typography>
            )}
        </Box>
    );
}

// Filter predicate — reuses getFeedbackDescriptor so a message matches a facet
// only if it would *display* as that facet. No re-parsing of rating strings.
function messageMatchesFacets(detail, feedbackType, facets) {
    if (!facets || facets.length === 0) return true;
    const descriptor = getFeedbackDescriptor(detail?.feedbackRating, feedbackType);
    const hasComment = Boolean(detail?.feedbackMessage);
    return facets.some((facet) => {
        if (facet === 'comment') return hasComment;
        if (facet === 'positive') return descriptor?.type === 'thumb' && descriptor.sentiment === 'positive';
        if (facet === 'negative') return descriptor?.type === 'thumb' && descriptor.sentiment === 'negative';
        if (facet.startsWith('star:')) return descriptor?.type === 'stars' && descriptor.value === Number(facet.slice(5));
        return false;
    });
}

function computeFacetCounts(messages, feedbackType) {
    const counts = { positive: 0, negative: 0, comment: 0, stars: {} };
    messages.forEach((detail) => {
        if (detail?.feedbackMessage) counts.comment += 1;
        const descriptor = getFeedbackDescriptor(detail?.feedbackRating, feedbackType);
        if (!descriptor) return;
        if (descriptor.type === 'thumb') {
            if (descriptor.sentiment === 'positive') counts.positive += 1;
            else if (descriptor.sentiment === 'negative') counts.negative += 1;
        } else if (descriptor.type === 'stars' && descriptor.value) {
            counts.stars[descriptor.value] = (counts.stars[descriptor.value] || 0) + 1;
        }
    });
    return counts;
}

// Build the chip set from what actually appears in this conversation, so the
// header auto-adapts to the chatbot's feedbackType (thumbs vs stars) plus comments.
function buildAvailableFacets(counts) {
    const facets = [];
    if (counts.positive) facets.push({ key: 'positive', kind: 'positive', count: counts.positive });
    if (counts.negative) facets.push({ key: 'negative', kind: 'negative', count: counts.negative });
    [1, 2, 3, 4, 5].forEach((n) => {
        if (counts.stars[n]) facets.push({ key: `star:${n}`, kind: 'star', value: n, count: counts.stars[n] });
    });
    if (counts.comment) facets.push({ key: 'comment', kind: 'comment', count: counts.comment });
    return facets;
}

// Pair-anchoring: a matching (bot) message keeps the run of user messages
// immediately preceding it visible, so an answer is never orphaned from its question.
function computeKeptIndices(messages, feedbackType, facets) {
    const kept = new Set();
    messages.forEach((detail, i) => {
        if (!messageMatchesFacets(detail, feedbackType, facets)) return;
        kept.add(i);
        let j = i - 1;
        while (j >= 0 && (messages[j]?.messageType || '').toLowerCase() === 'user') {
            kept.add(j);
            j -= 1;
        }
    });
    return kept;
}

// Group consecutive kept messages as-is; consecutive non-kept messages become a
// collapsible band (GitHub-diff style).
function buildRenderGroups(messages, filterActive, keptSet) {
    const groups = [];
    let band = null;
    messages.forEach((detail, i) => {
        const kept = !filterActive || (keptSet && keptSet.has(i));
        if (kept) {
            if (band) { groups.push(band); band = null; }
            groups.push({ type: 'message', detail, index: i });
        } else {
            if (!band) band = { type: 'band', key: `band-${i}`, items: [] };
            band.items.push({ detail, index: i });
        }
    });
    if (band) groups.push(band);
    return groups;
}

function csvEscape(value) {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (/[",\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
}

function downloadCsv(filename, rows) {
    const csv = rows.map(row => row.map(csvEscape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

const C = {
    crimson:      '#287bd6',
    crimsonLight: '#3f8fe0',
    blue:         '#1976d2',
    ink:          '#222222',
    muted:        '#6f6f6f',
    border:       '#e0e0e0',
    cardBg:       '#ffffff',
    pageBg:       '#ffffff',
    avatarBg:     '#bdbdbd',
    icon:         '#2b2b2b',
    iconMuted:    '#747474',
};

function ConversationHistory() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const page = parseInt(searchParams.get('page') || '1', 10);
    const pageSize = parseInt(searchParams.get('pageSize') || '20', 10);
    const search = searchParams.get('search') || '';
    const chatbotIds = searchParams.get('chatbotId') ? searchParams.get('chatbotId').split(',') : [];
    const startDateEpoch = searchParams.get('startDate') ? parseInt(searchParams.get('startDate'), 10) : dayjs().subtract(30, 'day').startOf('day').unix();
    const endDateEpoch = searchParams.get('endDate') ? parseInt(searchParams.get('endDate'), 10) : dayjs().endOf('day').unix();
    const startDate = dayjs.unix(startDateEpoch);
    const endDate = dayjs.unix(endDateEpoch);

    const [conversations, setConversations] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [chatbots, setChatbots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [searchInput, setSearchInput] = useState(search);
    const searchTimer = useRef(null);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedConversationDetails, setSelectedConversationDetails] = useState(null);
    const [activeConversationId, setActiveConversationId] = useState(null);

    const [citationPreview, setCitationPreview] = useState(null);

    // Rating filter state, scoped to the currently-open conversation drawer.
    const [feedbackFilter, setFeedbackFilter] = useState([]);
    const [expandedBands, setExpandedBands] = useState(() => new Set());

    const handleCitationPreview = async (documentId, pageNum, fileName) => {
        try {
            const token = (await import('../services/AuthService')).default.getAccessToken();
            const loginAs = (await import('../services/AuthService')).default.getLoginAs();
            const query = loginAs ? `?loginAs=${loginAs}` : '';
            const baseUrl = process.env.REACT_APP_CHATBOT_API_URL;
            const res = await fetch(`${baseUrl}private/conversations/history/citation/${documentId}/${pageNum}${query}`, {
                headers: { Authorization: `Bearer ${token}`, Accept: 'image/*' },
            });
            if (!res.ok) throw new Error('Preview not available');
            const previewBlob = await res.blob();
            if (!String(previewBlob.type || '').toLowerCase().startsWith('image/') || previewBlob.size === 0) {
                throw new Error('Preview endpoint did not return an image');
            }
            const url = URL.createObjectURL(previewBlob);
            setCitationPreview((current) => {
                if (current?.revoke && current.url) URL.revokeObjectURL(current.url);
                return { url, fileName, page: pageNum, previewType: 'image', revoke: true };
            });
        } catch (err) {
            console.error('Citation preview failed:', err);
        }
    };

    const closeCitationPreview = () => {
        setCitationPreview((current) => {
            if (current?.revoke && current.url) URL.revokeObjectURL(current.url);
            return null;
        });
    };

    const [rangeMenuEl, setRangeMenuEl] = useState(null);
    const [openCustom, setOpenCustom] = useState(false);
    const [tempFrom, setTempFrom] = useState(startDate);
    const [tempTo, setTempTo] = useState(endDate);
    const activeConversation = conversations.find(conv => conv.conversationId === activeConversationId);
    const activeChatbot = chatbots.find(bot => bot.chatbotId === activeConversation?.chatbotId);
    const formatDeviceCategory = (category) => category
        ? t(`conversationHistory.deviceCategories.${category}`, category)
        : '-';

    const updateParams = (newParams) => {
        const currentParams = Object.fromEntries(searchParams.entries());
        const merged = { ...currentParams, ...newParams };
        Object.keys(merged).forEach(key => {
            if (merged[key] === '' || merged[key] === null || merged[key] === undefined) {
                delete merged[key];
            }
        });
        setSearchParams(merged);
    };

    const fetchConversations = async (overrides = {}) => {
        try {
            setLoading(true);
            const params = {
                page,
                pageSize,
                startDate: startDateEpoch,
                endDate: endDateEpoch,
                ...(search && { search }),
                ...(chatbotIds.length && { chatbotId: chatbotIds.join(',') }),
                ...overrides,
            };
            const data = await conversationHistoryService.getConversations(params);
            setConversations(data.items || []);
            setTotalCount(data.total || 0);
        } catch (error) {
            console.error('Error fetching conversation history:', error);
        } finally {
            setLoading(false);
        }
    };

    const buildConversationParams = (overrides = {}) => ({
        page,
        pageSize,
        startDate: startDateEpoch,
        endDate: endDateEpoch,
        ...(search && { search }),
        ...(chatbotIds.length && { chatbotId: chatbotIds.join(',') }),
        ...overrides,
    });

    const handleDownloadCsv = async () => {
        try {
            setExporting(true);
            const exportPageSize = 100;
            const firstPage = await conversationHistoryService.getConversations(
                buildConversationParams({ page: 1, pageSize: exportPageSize })
            );
            const allConversations = [...(firstPage.items || [])];
            const total = firstPage.total || allConversations.length;
            const totalPages = Math.ceil(total / exportPageSize);

            for (let nextPage = 2; nextPage <= totalPages; nextPage += 1) {
                const data = await conversationHistoryService.getConversations(
                    buildConversationParams({ page: nextPage, pageSize: exportPageSize })
                );
                allConversations.push(...(data.items || []));
            }

            const rows = [[
                'Conversation ID',
                'Chatbot',
                'Chatbot ID',
                'Engine',
                'Device Category',
                'Operating System',
                'Browser',
                'Country',
                'Conversation Date',
                'Message Count',
                'Message ID',
                'Sender',
                'Message Date',
                'Message',
                'Feedback Rating',
                'Feedback Display',
                'Feedback Message',
            ]];

            for (const conversation of allConversations) {
                const detail = await conversationHistoryService.getConversationDetail(conversation.conversationId);
                const feedbackType = chatbots.find(bot => bot.chatbotId === conversation.chatbotId)?.feedbackType;
                const messages = (detail.messages || []).filter(message => (message.messageType || '').toLowerCase() !== 'system');

                if (messages.length === 0) {
                    rows.push([
                        conversation.conversationId,
                        conversation.chatbotName || '',
                        conversation.chatbotId || '',
                        conversation.vendor || '',
                        conversation.deviceCategory || '',
                        conversation.operatingSystem || '',
                        [conversation.browserName, conversation.browserVersion].filter(Boolean).join(' '),
                        conversation.country || '',
                        conversation.createdAt ? dayjs.unix(conversation.createdAt).format('YYYY/MM/DD HH:mm') : '',
                        conversation.messageCount || 0,
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                    ]);
                    continue;
                }

                messages.forEach((message) => {
                    const feedbackDescriptor = getFeedbackDescriptor(message.feedbackRating, feedbackType);
                    rows.push([
                        conversation.conversationId,
                        conversation.chatbotName || '',
                        conversation.chatbotId || '',
                        conversation.vendor || '',
                        conversation.deviceCategory || '',
                        conversation.operatingSystem || '',
                        [conversation.browserName, conversation.browserVersion].filter(Boolean).join(' '),
                        conversation.country || '',
                        conversation.createdAt ? dayjs.unix(conversation.createdAt).format('YYYY/MM/DD HH:mm') : '',
                        conversation.messageCount || messages.length,
                        message.conversationHistoryId || '',
                        message.messageType || '',
                        message.createdAt ? dayjs.unix(message.createdAt).format('YYYY/MM/DD HH:mm') : '',
                        message.messageContent || '',
                        message.feedbackRating || '',
                        feedbackDescriptor?.label || '',
                        message.feedbackMessage || '',
                    ]);
                });
            }

            downloadCsv(`conversation-history-${dayjs().format('YYYYMMDD-HHmm')}.csv`, rows);
        } catch (error) {
            console.error('Error exporting conversation history:', error);
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchConversations();
        chatbotService.getChatbots().then(data => setChatbots(data || [])).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            updateParams({ search: value, page: 1 });
            fetchConversations({ search: value || undefined, page: 1 });
        }, 300);
    };

    const handleRowClick = async (conversation) => {
        try {
            setActiveConversationId(conversation.conversationId);
            setIsDrawerOpen(true);
            setSelectedConversationDetails(null);
            setFeedbackFilter([]);
            setExpandedBands(new Set());
            const details = await conversationHistoryService.getConversationDetail(conversation.conversationId);
            setSelectedConversationDetails(details.messages || []);
        } catch (error) {
            console.error('Error fetching conversation details:', error);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setActiveConversationId(null);
        setFeedbackFilter([]);
        setExpandedBands(new Set());
    };

    const handleChangePage = (event, newPage) => {
        updateParams({ page: newPage + 1 });
        fetchConversations({ page: newPage + 1 });
    };

    const handleChangeRowsPerPage = (event) => {
        const newSize = parseInt(event.target.value, 10);
        updateParams({ pageSize: newSize, page: 1 });
        fetchConversations({ pageSize: newSize, page: 1 });
    };

    const handleChatbotChange = (event) => {
        const val = event.target.value;
        const selected = typeof val === 'string' ? val.split(',').filter(Boolean) : val;
        const param = selected.length ? selected.join(',') : '';
        updateParams({ chatbotId: param, page: 1 });
        fetchConversations({ chatbotId: param || undefined, page: 1 });
    };

    const applyDateRange = (from, to) => {
        updateParams({ startDate: from.unix(), endDate: to.unix(), page: 1 });
        fetchConversations({ startDate: from.unix(), endDate: to.unix(), page: 1 });
        setRangeMenuEl(null);
    };

    const filterControlSx = {
        height: 40,
        borderRadius: '6px',
        backgroundColor: C.cardBg,
        color: C.ink,
        fontSize: '0.925rem',
        '& fieldset': { borderColor: '#e1e4e8' },
        '&:hover fieldset': { borderColor: '#c8cdd4' },
        '&.Mui-focused fieldset': { borderColor: C.crimson },
    };
    const menuPaperSx = {
        mt: 1,
        border: '1px solid #dde2e8',
        borderRadius: '6px',
        backgroundColor: C.cardBg,
        boxShadow: '0 8px 18px rgba(20, 32, 50, 0.08), 0 1px 3px rgba(20, 32, 50, 0.06)',
        overflow: 'hidden',
    };
    const menuItemSx = {
        minHeight: 42,
        px: 1.7,
        py: 0.9,
        fontSize: '0.92rem',
        color: C.ink,
        '&:hover': { backgroundColor: '#f6f8fa' },
        '&.Mui-selected': {
            backgroundColor: 'rgba(40,123,214,0.08)',
            '&:hover': { backgroundColor: 'rgba(40,123,214,0.12)' },
        },
    };

    // ----- Conversation drawer: rating filter derivation -----
    const feedbackType = activeChatbot?.feedbackType;
    const detailMessages = selectedConversationDetails || [];
    const nonSystemMessages = detailMessages.filter(
        (d) => (d?.messageType || '').toLowerCase() !== 'system'
    );
    const facetCounts = computeFacetCounts(nonSystemMessages, feedbackType);
    const availableFacets = buildAvailableFacets(facetCounts);
    const filterActive = feedbackFilter.length > 0;
    const keptIndexSet = filterActive
        ? computeKeptIndices(nonSystemMessages, feedbackType, feedbackFilter)
        : null;
    const hasMatches =
        !filterActive || nonSystemMessages.some((d) => messageMatchesFacets(d, feedbackType, feedbackFilter));
    const renderGroups = buildRenderGroups(nonSystemMessages, filterActive, keptIndexSet);

    const toggleFacet = (key) => {
        setExpandedBands(new Set());
        setFeedbackFilter((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const clearFilter = () => {
        setFeedbackFilter([]);
        setExpandedBands(new Set());
    };

    const toggleBand = (key) => {
        setExpandedBands((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const facetPalette = {
        positive: { main: '#067647', onFilled: '#ffffff' },
        negative: { main: '#b42318', onFilled: '#ffffff' },
        star: { main: '#f59e0b', onFilled: '#3a2a00' },
        comment: { main: '#6f6f6f', onFilled: '#ffffff' },
    };

    const renderFacetChip = (facet) => {
        const selected = feedbackFilter.includes(facet.key);
        const colors = facetPalette[facet.kind];
        let icon;
        let tip;
        if (facet.kind === 'positive') {
            icon = <ThumbUpAltOutlinedIcon sx={{ fontSize: 15 }} />;
            tip = t('conversationHistory.thumbsUp', 'Thumbs up');
        } else if (facet.kind === 'negative') {
            icon = <ThumbDownAltOutlinedIcon sx={{ fontSize: 15 }} />;
            tip = t('conversationHistory.thumbsDown', 'Thumbs down');
        } else if (facet.kind === 'star') {
            icon = <StarIcon sx={{ fontSize: 15 }} />;
            tip = `${facet.value} ${facet.value === 1 ? t('conversationHistory.star', 'Star') : t('conversationHistory.stars', 'Stars')}`;
        } else {
            icon = <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />;
            tip = t('conversationHistory.hasComment', 'Has comment');
        }
        const label =
            facet.kind === 'star' ? (
                <span>
                    {facet.value}
                    <span style={{ opacity: 0.7, marginLeft: 4 }}>({facet.count})</span>
                </span>
            ) : (
                facet.count
            );
        return (
            <Tooltip key={facet.key} title={tip} arrow>
                <Chip
                    icon={icon}
                    label={label}
                    onClick={() => toggleFacet(facet.key)}
                    aria-pressed={selected}
                    sx={{
                        height: 26,
                        borderRadius: '13px',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: colors.main,
                        bgcolor: selected ? colors.main : 'transparent',
                        color: selected ? colors.onFilled : colors.main,
                        transition: 'background-color 0.15s, color 0.15s',
                        '& .MuiChip-icon': { color: 'inherit', ml: 0.6, mr: -0.3 },
                        '& .MuiChip-label': { px: 0.75 },
                        '&:hover': { bgcolor: selected ? colors.main : `${colors.main}14` },
                    }}
                />
            </Tooltip>
        );
    };

    const renderMessageBubble = (detail) => {
        const senderRole = (detail?.messageType || '').toLowerCase();
        const isUser = senderRole === 'user';
        const isBot = senderRole === 'bot' || senderRole === 'assistant';
        const isRightSide = isUser;
        const displayMessage =
            typeof detail?.messageContent === 'string'
                ? detail.messageContent
                : detail?.messageContent != null
                    ? String(detail.messageContent)
                    : '';

        return (
            <Box
                key={detail.conversationHistoryId}
                sx={{ display: 'flex', justifyContent: isRightSide ? 'flex-end' : 'flex-start', minWidth: 0 }}
            >
                <Box
                    sx={{
                        bgcolor: isUser ? 'primary.main' : 'grey.200',
                        color: isUser ? 'common.white' : 'text.primary',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        borderTopLeftRadius: isRightSide ? 8 : 0,
                        borderTopRightRadius: isRightSide ? 0 : 8,
                        maxWidth: '75%',
                        minWidth: 0,
                        overflow: 'hidden',
                        boxShadow: 1,
                    }}
                >
                    <Typography
                        variant="body2"
                        component="div"
                        sx={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            '& a': {
                                color: C.blue,
                                fontWeight: 500,
                                textDecoration: 'underline',
                                textDecorationColor: 'rgba(25,118,210,0.4)',
                                textUnderlineOffset: '2px',
                                wordBreak: 'break-all',
                                overflowWrap: 'anywhere',
                                transition: 'color 0.15s, text-decoration-color 0.15s',
                                '&:hover': { color: '#125ea6', textDecorationColor: '#125ea6' },
                            },
                            '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto', m: 0 },
                            '& code': { wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
                            '& h1': { fontSize: '1rem', fontWeight: 700, m: 0, mb: 0.5 },
                            '& h2': { fontSize: '0.95rem', fontWeight: 700, m: 0, mb: 0.5 },
                            '& h3': { fontSize: '0.9rem', fontWeight: 600, m: 0, mb: 0.5 },
                            '& p': { m: 0, mb: 0.75 },
                            '& p:last-child': { mb: 0 },
                            '& ul, & ol': { m: 0, pl: 2, mb: 0.75 },
                            '& li': { mb: 0.25 },
                            '& table': { display: 'block', width: '100%', overflowX: 'auto', borderCollapse: 'collapse', my: 0.75, fontSize: '0.8rem' },
                            '& th, & td': { border: '1px solid', borderColor: 'rgba(0,0,0,0.18)', px: 0.75, py: 0.4, textAlign: 'left' },
                            '& th': { fontWeight: 700, bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        {isBot ? (
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ node, children, ...props }) => (
                                        <a {...props} target="_blank" rel="noreferrer">
                                            {children}
                                        </a>
                                    ),
                                    p: ({ node, children }) => (
                                        <p>{typeof children === 'string' ? renderTextWithCitations(children, handleCitationPreview)
                                            : Array.isArray(children) ? children.map((child, i) =>
                                                typeof child === 'string' ? <React.Fragment key={i}>{renderTextWithCitations(child, handleCitationPreview)}</React.Fragment> : child
                                            ) : children}</p>
                                    ),
                                }}
                            >
                                {displayMessage}
                            </ReactMarkdown>
                        ) : (
                            displayMessage
                        )}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 0.5,
                            opacity: 0.8,
                            textAlign: isRightSide ? 'right' : 'left',
                        }}
                    >
                        {dayjs.unix(detail.createdAt).format('YYYY/MM/DD - HH:mm')}
                    </Typography>
                    <FeedbackDisplay
                        rating={detail.feedbackRating}
                        message={detail.feedbackMessage}
                        feedbackType={feedbackType}
                    />
                </Box>
            </Box>
        );
    };

    const renderCollapseBar = (key, count, expanded) => (
        <Box
            key={`${key}-bar`}
            role="button"
            tabIndex={0}
            onClick={() => toggleBand(key)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBand(key); } }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                py: 0.25,
                '&:hover .cbar-pill': {
                    bgcolor: 'rgba(40,123,214,0.08)',
                    color: C.crimson,
                    borderColor: 'rgba(40,123,214,0.35)',
                },
            }}
        >
            <Box sx={{ flex: 1, height: '1px', bgcolor: C.border }} />
            <Box
                className="cbar-pill"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 1,
                    py: 0.25,
                    border: `1px solid ${C.border}`,
                    borderRadius: '12px',
                    bgcolor: C.cardBg,
                    color: C.muted,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
                }}
            >
                {expanded ? <UnfoldLessIcon sx={{ fontSize: 15 }} /> : <UnfoldMoreIcon sx={{ fontSize: 15 }} />}
                {expanded
                    ? t('conversationHistory.hideBand', 'Hide {{count}}', { count })
                    : t('conversationHistory.showHiddenBand', 'Show {{count}} hidden', { count })}
            </Box>
            <Box sx={{ flex: 1, height: '1px', bgcolor: C.border }} />
        </Box>
    );

    const renderBand = (band) => {
        const expanded = expandedBands.has(band.key);
        const count = band.items.length;
        if (expanded) {
            return (
                <React.Fragment key={band.key}>
                    {renderCollapseBar(band.key, count, true)}
                    {band.items.map((item) => renderMessageBubble(item.detail))}
                </React.Fragment>
            );
        }
        return renderCollapseBar(band.key, count, false);
    };

    return (
        <Box
            sx={{
                mx: -3,
                mt: -3,
                mb: -3,
                width: 'calc(100% + 48px)',
                px: { xs: 2, md: 5 },
                pt: 4,
                pb: 3,
                backgroundColor: C.pageBg,
                minHeight: '100vh',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Box sx={{ minWidth: 220 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
                            {t('sideBar.conversationHistory', 'Conversation History')}
                        </Typography>
                        <HelpButton />
                    </Box>
                </Box>

                <Tooltip title={t('conversationHistory.exportCsvTooltip', 'Export filtered conversations as CSV')} arrow>
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={exporting ? <CircularProgress size={16} thickness={4} /> : <FileDownloadOutlinedIcon />}
                            onClick={handleDownloadCsv}
                            disabled={exporting || loading || totalCount === 0}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '6px',
                                height: 38,
                                borderColor: '#e1e4e8',
                                color: C.ink,
                                backgroundColor: C.cardBg,
                                fontWeight: 600,
                                px: 1.5,
                                '& .MuiButton-startIcon': { color: C.iconMuted },
                                '&:hover': { borderColor: '#c8cdd4', backgroundColor: '#fafbfc' },
                            }}
                        >
                            {exporting ? t('conversationHistory.exporting', 'Exporting...') : t('conversationHistory.exportCsv', 'Export CSV')}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: '1fr 1fr 1.5fr',
                        lg: '1fr 1fr 1.5fr',
                    },
                    gap: 1.2,
                    alignItems: 'center',
                    mb: 2.6,
                }}
            >
                <Box>
                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 19 }} />}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 19, color: C.iconMuted }} />}
                        onClick={(e) => setRangeMenuEl(e.currentTarget)}
                        sx={{
                            textTransform: 'none',
                            justifyContent: 'space-between',
                            height: 40,
                            borderRadius: '6px',
                            borderColor: '#e1e4e8',
                            color: C.ink,
                            backgroundColor: C.cardBg,
                            fontSize: '0.925rem',
                            fontWeight: 500,
                            px: 1.4,
                            '& .MuiButton-startIcon': { mr: 1, color: C.iconMuted },
                            '& .MuiButton-endIcon': { ml: 1, mr: 0 },
                            '&:hover': { borderColor: '#c8cdd4', backgroundColor: '#fafbfc' }
                        }}
                    >
                        <Box component="span" sx={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {`${startDate.format('YYYY/MM/DD')} - ${endDate.format('YYYY/MM/DD')}`}
                        </Box>
                    </Button>
                    <Menu
                        anchorEl={rangeMenuEl}
                        open={Boolean(rangeMenuEl)}
                        onClose={() => setRangeMenuEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{ elevation: 0, sx: { ...menuPaperSx, minWidth: 236 } }}
                        MenuListProps={{ sx: { py: 0.6 } }}
                    >
                        <MenuItem sx={menuItemSx} onClick={() => applyDateRange(dayjs().startOf('day'), dayjs().endOf('day'))}>
                            {t('conversationHistory.today', 'Today')}
                        </MenuItem>
                        <MenuItem sx={menuItemSx} onClick={() => applyDateRange(dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day'))}>
                            {t('conversationHistory.yesterday', 'Yesterday')}
                        </MenuItem>
                        <MenuItem sx={menuItemSx} onClick={() => applyDateRange(dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day'))}>
                            {t('conversationHistory.last7', 'Last 7 days')}
                        </MenuItem>
                        <MenuItem sx={menuItemSx} onClick={() => applyDateRange(dayjs().subtract(14, 'day').startOf('day'), dayjs().endOf('day'))}>
                            {t('conversationHistory.last14', 'Last 14 days')}
                        </MenuItem>
                        <MenuItem sx={menuItemSx} onClick={() => applyDateRange(dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day'))}>
                            {t('conversationHistory.last30', 'Last 30 days')}
                        </MenuItem>
                        <Divider sx={{ my: 0.6, borderColor: '#edf0f3' }} />
                        <MenuItem sx={menuItemSx} onClick={() => { setTempFrom(startDate); setTempTo(endDate); setOpenCustom(true); setRangeMenuEl(null); }}>
                            {t('conversationHistory.custom', 'Custom')}
                        </MenuItem>
                    </Menu>
                </Box>

                <FormControl size="small" fullWidth>
                    <Select
                        multiple
                        autoWidth
                        value={chatbotIds}
                        onChange={handleChatbotChange}
                        displayEmpty
                        MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: {
                                elevation: 0,
                                sx: {
                                    ...menuPaperSx,
                                    minWidth: '300px !important',
                                    width: 'min(380px, calc(100vw - 32px))',
                                    maxHeight: 380,
                                },
                            },
                            MenuListProps: { sx: { py: 0.6 } },
                        }}
                        renderValue={(selected) => {
                            if (selected.length === 0) return t('conversationHistory.allChatbots', 'All Chatbots');
                            if (selected.length === 1) {
                                const bot = chatbots.find(b => b.chatbotId === selected[0]);
                                return bot?.chatbotName || selected[0];
                            }
                            return `${selected.length} chatbots selected`;
                        }}
                        sx={{
                            ...filterControlSx,
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                height: 40,
                                py: 0,
                                fontWeight: 500,
                            },
                        }}
                    >
                        <MenuItem
                            dense
                            sx={{ ...menuItemSx, fontWeight: 600 }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                const allIds = chatbots.map(b => b.chatbotId);
                                const allSelected = chatbotIds.length === chatbots.length;
                                const next = allSelected ? [] : allIds;
                                const param = next.join(',');
                                updateParams({ chatbotId: param, page: 1 });
                                fetchConversations({ chatbotId: param || undefined, page: 1 });
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={chatbots.length > 0 && chatbotIds.length === chatbots.length}
                                indeterminate={chatbotIds.length > 0 && chatbotIds.length < chatbots.length}
                            />
                            <ListItemText primary={chatbotIds.length === chatbots.length ? 'Deselect All' : 'Select All'} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                        </MenuItem>
                        <Divider sx={{ my: 0.6, borderColor: '#edf0f3' }} />
                        {chatbots.map((bot) => (
                            <MenuItem key={bot.chatbotId} value={bot.chatbotId} dense sx={menuItemSx}>
                                <Checkbox size="small" checked={chatbotIds.includes(bot.chatbotId)} />
                                <ListItemText
                                    primary={bot.chatbotName || bot.chatbotId}
                                    primaryTypographyProps={{
                                        noWrap: true,
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    placeholder={t('conversationHistory.search', 'Search conversations...')}
                    value={searchInput}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 22, color: '#7a7a7a' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            ...filterControlSx,
                            '& input': {
                                py: 0,
                                height: 40,
                                boxSizing: 'border-box',
                            },
                        },
                    }}
                />
            </Box>

            {loading && conversations.length === 0 ? (
                <Paper elevation={0} sx={{ textAlign: 'center', py: 8, borderRadius: '10px', border: `1px dashed ${C.border}` }}>
                    <CircularProgress size={30} thickness={4} sx={{ mb: 2 }} />
                    <Typography sx={{ color: C.muted, fontSize: '0.9rem' }}>
                        {t('conversationHistory.loading', 'Loading conversations...')}
                    </Typography>
                </Paper>
            ) : conversations.length === 0 ? (
                <Paper elevation={0} sx={{ textAlign: 'center', py: 8, borderRadius: '10px', border: `1px dashed ${C.border}` }}>
                    <Typography sx={{ color: C.muted, fontSize: '0.9rem' }}>
                        {search ? t('conversationHistory.noResults', 'No matching conversations found.') : t('conversationHistory.noConversations', 'No conversations yet.')}
                    </Typography>
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '14px',
                        border: '1px solid #dfe3e8',
                        overflow: 'hidden',
                        position: 'relative',
                        bgcolor: '#fff',
                    }}
                >
                    {loading && (
                        <>
                            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3, height: 3 }} />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255,255,255,0.54)',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: '4px', bgcolor: C.cardBg, boxShadow: 2 }}>
                                    <CircularProgress size={18} thickness={4} />
                                    <Typography sx={{ fontSize: '0.85rem', color: C.muted, fontWeight: 600 }}>
                                        {t('conversationHistory.applyingFilters', 'Applying filters...')}
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 1120 }}>
                            <TableHead
                                sx={{
                                    '& .MuiTableCell-root': {
                                        bgcolor: '#f7f8fa !important',
                                        color: '#687386',
                                        borderBottom: '1px solid #dfe3e8',
                                        fontSize: '0.78rem',
                                        fontWeight: '700 !important',
                                        letterSpacing: '0.01em',
                                        py: 1.75,
                                    },
                                }}
                            >
                                <TableRow>
                                    <TableCell>{t('conversationHistory.chatbot', 'Chatbot')}</TableCell>
                                    <TableCell>{t('conversationHistory.vendor', 'Engine')}</TableCell>
                                    <TableCell>{t('conversationHistory.firstMessage', 'First Message')}</TableCell>
                                    <TableCell>{t('conversationHistory.device', 'Device')}</TableCell>
                                    <TableCell>{t('conversationHistory.operatingSystem', 'OS')}</TableCell>
                                    <TableCell>{t('conversationHistory.browser', 'Browser')}</TableCell>
                                    <TableCell>{t('conversationHistory.country', 'Country')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                                        {t('conversationHistory.date', 'Date')}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>{t('conversationHistory.messages', 'Messages')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    '& .MuiTableCell-root': {
                                        color: '#182033',
                                        borderBottom: '1px solid #e8ebef',
                                        fontSize: '0.9rem',
                                        py: 2.15,
                                    },
                                    '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                                        borderBottom: 0,
                                    },
                                    '& .MuiTableCell-root:first-of-type': {
                                        fontWeight: 700,
                                    },
                                }}
                            >
                                {conversations.map((conv) => (
                                    <TableRow
                                        key={conv.conversationId}
                                        hover
                                        onClick={() => handleRowClick(conv)}
                                        selected={activeConversationId === conv.conversationId}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: activeConversationId === conv.conversationId ? '#fff7f2' : '#fff',
                                            transition: 'background-color 120ms ease',
                                            '&:hover': { bgcolor: activeConversationId === conv.conversationId ? '#fff3eb' : '#fafbfc' },
                                            '&.Mui-selected, &.Mui-selected:hover': {
                                                bgcolor: activeConversationId === conv.conversationId ? '#fff7f2' : '#fff',
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            {conv.chatbotName || conv.chatbotId}
                                            {conv.callerPhoneNumber && <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>{t('conversationHistory.callerPhoneNumber', 'Caller')}: {conv.callerPhoneNumber}</Typography>}
                                        </TableCell>
                                        <TableCell>{conv.vendor && <Chip label={conv.vendor} size="small" sx={{ fontSize: '0.75rem', height: 20 }} />}</TableCell>
                                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{conv.firstMessage || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDeviceCategory(conv.deviceCategory)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{conv.operatingSystem || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{[conv.browserName, conv.browserVersion].filter(Boolean).join(' ') || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{conv.country || '-'}</TableCell>
                                        <TableCell>
                                            {dayjs.unix(conv.createdAt).format('YYYY/MM/DD - HH:mm')}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{conv.messageCount || 0}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page - 1}
                        onPageChange={handleChangePage}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        sx={{ borderTop: '1px solid #e8ebef', color: '#687386' }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: C.muted, px: 2, pb: 1.5 }}>
                        {t('conversationHistory.ipGeolocationAttributionPrefix', 'NetBot uses the')}{' '}
                        <a href="https://www.ip2location.com" target="_blank" rel="noopener noreferrer">IP2Location LITE</a>{' '}
                        {t('conversationHistory.ipGeolocationAttributionSuffix', 'database for IP geolocation.')}
                    </Typography>
                </Paper>
            )}

            <Drawer anchor="right" open={isDrawerOpen} onClose={handleDrawerClose}>
                <Box sx={{ width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', p: 2, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{t('conversationHistory.details', 'Conversation Details')}</Typography>
                    {activeConversation?.callerPhoneNumber && (
                        <Typography variant="body2" sx={{ color: C.muted, mb: 1 }}>
                            {t('conversationHistory.callerPhoneNumber', 'Caller')}: {activeConversation.callerPhoneNumber}
                        </Typography>
                    )}
                    {activeConversation?.deviceCategory && (
                        <Typography variant="body2" sx={{ color: C.muted, mb: 0.5, textTransform: 'capitalize' }}>
                            {t('conversationHistory.device', 'Device')}: {formatDeviceCategory(activeConversation.deviceCategory)}
                        </Typography>
                    )}
                    {activeConversation?.operatingSystem && (
                        <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                            {t('conversationHistory.operatingSystem', 'OS')}: {activeConversation.operatingSystem}
                        </Typography>
                    )}
                    {activeConversation?.browserName && (
                        <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                            {t('conversationHistory.browser', 'Browser')}: {[activeConversation.browserName, activeConversation.browserVersion].filter(Boolean).join(' ')}
                        </Typography>
                    )}
                    {activeConversation?.country && (
                        <Typography variant="body2" sx={{ color: C.muted, mb: 1.5 }}>
                            {t('conversationHistory.country', 'Country')}: {activeConversation.country}
                        </Typography>
                    )}
                    {availableFacets.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                            <Typography variant="caption" sx={{ color: C.muted, fontWeight: 700, mr: 0.25 }}>
                                {t('conversationHistory.filterByRating', 'Filter')}
                            </Typography>
                            {availableFacets.map((facet) => renderFacetChip(facet))}
                            {filterActive && (
                                <Button
                                    size="small"
                                    onClick={clearFilter}
                                    sx={{ textTransform: 'none', minWidth: 0, px: 0.75, color: C.muted, fontSize: '0.75rem', fontWeight: 600 }}
                                >
                                    {t('conversationHistory.clear', 'Clear')}
                                </Button>
                            )}
                        </Box>
                    )}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pr: 1 }}>
                        {!selectedConversationDetails ? (
                            <Typography sx={{ color: C.muted, textAlign: 'center', mt: 4 }}>{t('conversationHistory.loadingDetails', 'Loading...')}</Typography>
                        ) : nonSystemMessages.length === 0 ? (
                            <Typography sx={{ color: C.muted, textAlign: 'center', mt: 4 }}>{t('conversationHistory.noMessages', 'No messages found.')}</Typography>
                        ) : filterActive && !hasMatches ? (
                            <Typography sx={{ color: C.muted, textAlign: 'center', mt: 4 }}>
                                {t('conversationHistory.noRatingMatch', 'No messages match this filter.')}
                            </Typography>
                        ) : (
                            renderGroups.map((group) =>
                                group.type === 'message' ? renderMessageBubble(group.detail) : renderBand(group)
                            )
                        )}
                    </Box>
                </Box>
            </Drawer>

            <MuiDialog open={openCustom} onClose={() => setOpenCustom(false)}>
                <DialogTitle>{t('conversationHistory.custom', 'Custom')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', gap: 2, mt: 1, px: 3, pt: 2.5, pb: 2, flexDirection: { xs: 'column', sm: 'row' }, overflow: 'visible' }}>
                    <TextField
                        label={t('conversationHistory.from', 'From')}
                        type="date"
                        size="small"
                        value={tempFrom ? tempFrom.format('YYYY-MM-DD') : ''}
                        onChange={(e) => setTempFrom(dayjs(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: { xs: '100%', sm: 220 },
                            '& .MuiInputLabel-root': {
                                bgcolor: 'background.paper',
                                px: 0.5,
                            },
                        }}
                    />
                    <TextField
                        label={t('conversationHistory.to', 'To')}
                        type="date"
                        size="small"
                        value={tempTo ? tempTo.format('YYYY-MM-DD') : ''}
                        onChange={(e) => setTempTo(dayjs(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: { xs: '100%', sm: 220 },
                            '& .MuiInputLabel-root': {
                                bgcolor: 'background.paper',
                                px: 0.5,
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCustom(false)}>{t('conversation.cancel', 'Cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (tempFrom && tempTo) {
                                applyDateRange(tempFrom.startOf('day'), tempTo.endOf('day'));
                            }
                            setOpenCustom(false);
                        }}
                    >
                        {t('conversationHistory.apply', 'Apply')}
                    </Button>
                </DialogActions>
            </MuiDialog>

            <MuiDialog
                open={Boolean(citationPreview)}
                onClose={closeCitationPreview}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
            >
                {citationPreview && (
                    <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
                            <DescriptionIcon sx={{ color: C.blue, fontSize: 20 }} />
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, flex: 1 }} noWrap>
                                {citationPreview.fileName}
                            </Typography>
                            <Chip label={`Page ${citationPreview.page}`} size="small" sx={{ fontSize: '0.75rem' }} />
                        </DialogTitle>
                        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5', minHeight: 400 }}>
                            {citationPreview.previewType === 'pdf' ? (
                                <iframe
                                    src={`${citationPreview.url}#page=${citationPreview.page}`}
                                    title={`${citationPreview.fileName} - Page ${citationPreview.page}`}
                                    style={{ width: '100%', height: '70vh', border: 'none' }}
                                />
                            ) : (
                                <img
                                    src={citationPreview.url}
                                    alt={`${citationPreview.fileName} - Page ${citationPreview.page}`}
                                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                    onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = `<p style="padding:40px;color:#999;text-align:center">${t('conversationHistory.previewUnavailable', 'Preview not available for this page.')}</p>`; }}
                                />
                            )}
                        </DialogContent>
                    </>
                )}
            </MuiDialog>
        </Box>
    );
}

export default ConversationHistory;
